/**
 * FilterSection Component
 * 라이더 근무 현황 검색 필터 영역
 */
import React, { useCallback, useMemo } from 'react';
import DateRangePicker from '@/shared/components/forms/DateRangePicker';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import { RiderWorkStatusFilters, Supplier } from '../types/riderWorkStatus.types';
import '@/shared/styles/search-form.css';

/** 기간 프리셋 타입 */
type DatePreset = 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth';

interface FilterSectionProps {
    filters: RiderWorkStatusFilters;
    suppliers: Supplier[];
    suppliersLoading: boolean;
    isSupplierUser: boolean;
    loading: boolean;
    excelDownloading: boolean;
    onFilterChange: (key: keyof RiderWorkStatusFilters, value: string) => void;
    onSearch: () => void;
    onExcelDownload: () => void;
}

/**
 * 날짜를 YYYY-MM-DD 형식으로 변환 (DateInput용)
 * 필터에 저장된 YYYYMMDD 형식을 YYYY-MM-DD로 변환
 */
const formatDateToYYYYMMDD = (date: string): string => {
    if (!date) return '';
    // YYYYMMDD 형식이면 YYYY-MM-DD로 변환
    if (date.length === 8 && !date.includes('-')) {
        return `${date.substring(0, 4)}-${date.substring(4, 6)}-${date.substring(6, 8)}`;
    }
    // 이미 YYYY-MM-DD 형식이면 그대로 반환
    return date;
};

/**
 * 날짜를 YYYYMMDD 형식으로 변환 (필터 저장용)
 * DateInput에서 받은 YYYY-MM-DD 형식을 YYYYMMDD로 변환
 */
const formatDateFromYYYYMMDD = (date: string): string => {
    if (!date) return '';
    // YYYY-MM-DD 형식이면 YYYYMMDD로 변환
    return date.replace(/-/g, '');
};

/**
 * Date를 YYYYMMDD 형식 문자열로 변환
 */
const formatDateToStorage = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
};

const FilterSection: React.FC<FilterSectionProps> = ({
    filters,
    suppliers,
    suppliersLoading,
    isSupplierUser,
    loading,
    excelDownloading,
    onFilterChange,
    onSearch,
    onExcelDownload
}) => {
    /**
     * 날짜 변경 핸들러
     */
    const handleDateChange = (key: 'fromDate' | 'toDate', value: string) => {
        const formattedDate = formatDateFromYYYYMMDD(value);
        onFilterChange(key, formattedDate);
    };

    /**
     * 엔터키 입력 시 검색 실행
     */
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            onSearch();
        }
    };

    /**
     * 기간 프리셋 버튼 클릭 핸들러
     */
    const handlePresetClick = useCallback((preset: DatePreset) => {
        const today = new Date();
        let startDate: Date;
        let endDate: Date;

        switch (preset) {
            case 'thisWeek': {
                // 이번 주 (월요일 ~ 오늘)
                const dayOfWeek = today.getDay();
                const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                startDate = new Date(today);
                startDate.setDate(today.getDate() + diffToMonday);
                endDate = today;
                break;
            }
            case 'lastWeek': {
                // 지난 주 (월요일 ~ 일요일)
                const dayOfWeek = today.getDay();
                const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                startDate = new Date(today);
                startDate.setDate(today.getDate() + diffToMonday - 7);
                endDate = new Date(startDate);
                endDate.setDate(startDate.getDate() + 6);
                break;
            }
            case 'thisMonth': {
                // 이번 달 (1일 ~ 오늘)
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = today;
                break;
            }
            case 'lastMonth': {
                // 지난 달 (1일 ~ 말일)
                startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                endDate = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            }
        }

        onFilterChange('fromDate', formatDateToStorage(startDate));
        onFilterChange('toDate', formatDateToStorage(endDate));
    }, [onFilterChange]);

    /**
     * 현재 선택된 프리셋 확인
     */
    const activePreset = useMemo((): DatePreset | null => {
        if (!filters.fromDate || !filters.toDate) return null;

        const today = new Date();
        const dayOfWeek = today.getDay();
        const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

        // 이번 주 (월요일 ~ 오늘)
        const thisWeekStart = new Date(today);
        thisWeekStart.setDate(today.getDate() + diffToMonday);
        if (filters.fromDate === formatDateToStorage(thisWeekStart) && filters.toDate === formatDateToStorage(today)) {
            return 'thisWeek';
        }

        // 지난 주 (월요일 ~ 일요일)
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() + diffToMonday - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        if (filters.fromDate === formatDateToStorage(lastWeekStart) && filters.toDate === formatDateToStorage(lastWeekEnd)) {
            return 'lastWeek';
        }

        // 이번 달 (1일 ~ 오늘)
        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        if (filters.fromDate === formatDateToStorage(thisMonthStart) && filters.toDate === formatDateToStorage(today)) {
            return 'thisMonth';
        }

        // 지난 달 (1일 ~ 말일)
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        if (filters.fromDate === formatDateToStorage(lastMonthStart) && filters.toDate === formatDateToStorage(lastMonthEnd)) {
            return 'lastMonth';
        }

        return null;
    }, [filters.fromDate, filters.toDate]);

    // 협력사 옵션을 SelectOption 형식으로 변환
    const supplierOptions: SelectOption[] = useMemo(() => {
        return suppliers.map((supplier) => ({
            value: supplier.centerId,
            label: supplier.name
        }));
    }, [suppliers]);

    return (
        <div className="card search-card">
            <div className="card-body">
                <div className={`search-form work-status-search-form ${isSupplierUser ? 'supplier-view' : ''}`}>
                    {/* 조회 기간 */}
                    <div className="form-group date-range-group">
                        <div className="form-field">
                            <div className="form-label-row">
                                <label className="form-label">
                                    <i className="fas fa-calendar-alt" />
                                    조회 기간
                                </label>
                                {/* 기간 프리셋 버튼 */}
                                <div className="date-preset-buttons">
                                    <button
                                        type="button"
                                        className={`date-preset-btn ${activePreset === 'thisWeek' ? 'active' : ''}`}
                                        onClick={() => handlePresetClick('thisWeek')}
                                    >
                                        이번 주
                                    </button>
                                    <button
                                        type="button"
                                        className={`date-preset-btn ${activePreset === 'lastWeek' ? 'active' : ''}`}
                                        onClick={() => handlePresetClick('lastWeek')}
                                    >
                                        지난 주
                                    </button>
                                    <button
                                        type="button"
                                        className={`date-preset-btn ${activePreset === 'thisMonth' ? 'active' : ''}`}
                                        onClick={() => handlePresetClick('thisMonth')}
                                    >
                                        이번 달
                                    </button>
                                    <button
                                        type="button"
                                        className={`date-preset-btn ${activePreset === 'lastMonth' ? 'active' : ''}`}
                                        onClick={() => handlePresetClick('lastMonth')}
                                    >
                                        지난 달
                                    </button>
                                </div>
                            </div>
                            <DateRangePicker
                                startDate={formatDateToYYYYMMDD(filters.fromDate)}
                                endDate={formatDateToYYYYMMDD(filters.toDate)}
                                onStartDateChange={(value) => handleDateChange('fromDate', value)}
                                onEndDateChange={(value) => handleDateChange('toDate', value)}
                                startLabel="시작일"
                                endLabel="종료일"
                            />
                        </div>
                    </div>

                    {/* 협력사 (본사 사용자만) */}
                    {!isSupplierUser && (
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-building" />
                                    협력사
                                </label>
                                <SearchableSelect
                                    options={supplierOptions}
                                    value={filters.suppId || ''}
                                    onChange={(value) => onFilterChange('suppId', value)}
                                    placeholder={suppliersLoading ? '로딩 중...' : '협력사 검색...'}
                                    disabled={suppliersLoading}
                                    showAllOption={true}
                                    allOptionLabel="전체"
                                />
                            </div>
                        </div>
                    )}

                    {/* 최소 근무일수 필터 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-calendar-check" />
                                최소 근무일수
                            </label>
                            <input
                                type="number"
                                className="text-input work-days-input"
                                value={filters.minWorkDays || ''}
                                onChange={(e) => onFilterChange('minWorkDays', e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="0"
                                min="0"
                            />
                        </div>
                    </div>

                    {/* 버튼 그룹 */}
                    <div className="form-group button-group">
                        <button
                            className="btn btn-success excel-btn"
                            onClick={onExcelDownload}
                            disabled={excelDownloading || !filters.fromDate || !filters.toDate}
                        >
                            {excelDownloading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    다운로드 중...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-file-excel"></i>
                                    엑셀
                                </>
                            )}
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={onSearch}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    조회 중...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-search"></i>
                                    검색
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterSection;

